import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import "./version-center.css";
import "./version-center-audit.css";

type Check = {
  id: string;
  name: string;
  status: string;
  detail: string | null;
};
type Version = {
  id: string;
  version_number: string;
  title: string;
  description: string;
  status: string;
  artifact_name: string;
  artifact_path: string;
  artifact_sha256: string;
  created_at: string;
  scheduled_for: string | null;
  maintenance_minutes: number;
  checks: Check[];
};
type Environment = {
  environment_key: "testes" | "producao";
  display_name: string;
  public_url: string | null;
  current_version: string | null;
  active: boolean;
};
type VersionEvent = {
  id: number;
  version_id: string | null;
  event_type: string;
  detail: Record<string, unknown>;
  created_at: string;
  actor_name: string;
};
type Panel = {
  environments: Environment[];
  permissions: { manage: boolean; publish_production: boolean };
  versions: Version[];
  events: VersionEvent[];
};
const labels: Record<string, string> = {
  draft: "Rascunho",
  in_tests: "Em Testes",
  corrections: "Correções necessárias",
  approved: "Aprovada",
  scheduled: "Programada",
  publishing: "Em publicação",
  published: "Publicada",
  failed: "Falhou",
  reverted: "Revertida",
};
const hash = async (file: File) =>
  Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
    ),
  )
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
const list = (value: string) =>
  value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

export function VersionCenter() {
  const [panel, setPanel] = useState<Panel | null>(null),
    [section, setSection] = useState<"overview" | "versions" | "publication">(
      "overview",
    ),
    [file, setFile] = useState<File | null>(null),
    [loading, setLoading] = useState(true),
    [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({
    version: "",
    title: "",
    description: "",
    migrations: "",
    functions: "",
    rollback:
      "Repor a release anterior, validar a base de dados e repetir o percurso crítico.",
  });
  const [schedule, setSchedule] = useState({
    id: "",
    date: "",
    minutes: "10",
    message:
      "O Vorno Stand será atualizado e poderá ficar temporariamente indisponível.",
  });
  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("version_center_panel");
    setLoading(false);
    if (error) setNotice(error.message);
    else setPanel(data as Panel);
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 10000);
    return () => clearTimeout(timer);
  }, [notice]);
  const pick = (event: ChangeEvent<HTMLInputElement>) =>
    setFile(event.target.files?.[0] ?? null);
  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (!file) return setNotice("Escolha o artefacto desta versão.");
    setLoading(true);
    try {
      const signature = await hash(file),
        path = `${form.version}/${signature.slice(0, 16)}-${file.name}`,
        upload = await supabase.storage
          .from("application-releases")
          .upload(path, file, {
            contentType: file.type || "application/octet-stream",
          });
      if (upload.error) throw upload.error;
      const result = await supabase.rpc("create_application_version", {
        p_version: form.version,
        p_title: form.title,
        p_description: form.description,
        p_artifact_name: file.name,
        p_artifact_path: path,
        p_sha256: signature,
        p_size: file.size,
        p_migrations: list(form.migrations),
        p_edge_functions: list(form.functions),
        p_rollback_plan: form.rollback,
      });
      if (result.error) {
        await supabase.storage.from("application-releases").remove([path]);
        throw result.error;
      }
      setFile(null);
      setForm({
        version: "",
        title: "",
        description: "",
        migrations: "",
        functions: "",
        rollback:
          "Repor a release anterior, validar a base de dados e repetir o percurso crítico.",
      });
      setNotice("Versão registada com artefacto privado e assinatura SHA-256.");
      await load();
    } catch (error) {
      setNotice(
        error instanceof Error
          ? error.message
          : "Não foi possível registar a versão.",
      );
    } finally {
      setLoading(false);
    }
  };
  const state = async (id: string, status: string) => {
    const { error } = await supabase.rpc("set_application_version_status", {
      p_id: id,
      p_status: status,
      p_note: null,
    });
    setNotice(error?.message ?? "Estado da versão atualizado.");
    if (!error) await load();
  };
  const check = async (item: Check) => {
    const next = item.status === "approved" ? "pending" : "approved",
      { error } = await supabase.rpc("set_application_version_check", {
        p_check_id: item.id,
        p_status: next,
        p_detail: null,
      });
    setNotice(error?.message ?? null);
    if (!error) await load();
  };
  const download = async (version: Version) => {
    const { data, error } = await supabase.storage
      .from("application-releases")
      .createSignedUrl(version.artifact_path, 120);
    if (error || !data)
      return setNotice(error?.message ?? "Artefacto indisponível.");
    window.location.assign(data.signedUrl);
  };
  const install = async (
    version: Version,
    environment: "testes" | "producao",
  ) => {
    const { error } = await supabase.rpc(
      "confirm_application_version_installation",
      { p_id: version.id, p_environment: environment },
    );
    setNotice(
      error?.message ??
        `Instalação em ${environment === "testes" ? "Testes" : "Produção"} registada depois da confirmação técnica.`,
    );
    if (!error) await load();
  };
  const plan = async () => {
    if (!schedule.date) return setNotice("Indique a data e a hora.");
    const value = new Date(schedule.date);
    if (Number.isNaN(value.getTime()))
      return setNotice("A data indicada não é válida.");
    const { error } = await supabase.rpc("schedule_application_version", {
      p_id: schedule.id,
      p_publish_at: value.toISOString(),
      p_maintenance_minutes: Number(schedule.minutes),
      p_message: schedule.message,
    });
    setNotice(
      error?.message ??
        "Publicação programada e janela de manutenção registada.",
    );
    if (!error) {
      setSchedule((current) => ({ ...current, id: "" }));
      await load();
    }
  };
  if (loading && !panel)
    return (
      <section className="version-center-loading">
        A preparar o Centro de Versões…
      </section>
    );
  if (!panel)
    return (
      <section className="version-center-loading">
        {notice ?? "Centro de Versões indisponível."}
      </section>
    );
  return (
    <section className="version-center">
      <header>
        <div>
          <p className="eyebrow">Gestão de versões</p>
          <p>
            Registe o artefacto, comprove cada validação e promova exatamente a
            mesma versão de Testes para Produção.
          </p>
        </div>
      </header>
      {notice && (
        <button className="notice" role="status" aria-live="polite" onClick={() => setNotice(null)}>
          {notice} ×
        </button>
      )}
      <div
        className="version-tabs"
        role="tablist"
        aria-label="Áreas do Centro de Versões"
      >
        {[
          {
            id: "overview",
            name: "Visão geral",
            detail: "Ambientes e versões atuais",
          },
          {
            id: "versions",
            name: "Versões e validações",
            detail: "Carregar, testar e aprovar",
          },
          {
            id: "publication",
            name: "Publicação",
            detail: "Programar e confirmar",
          },
        ].map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={section === item.id}
            className={section === item.id ? "active" : ""}
            onClick={() => setSection(item.id as typeof section)}
          >
            <strong>{item.name}</strong>
            <small>{item.detail}</small>
          </button>
        ))}
      </div>
      {section === "overview" && (
        <>
          <div className="environment-grid">
            {panel.environments.map((item) => (
              <article
                key={item.environment_key}
                className={item.environment_key}
              >
                <span>
                  {item.environment_key === "testes" ? "TESTES" : "PRODUÇÃO"}
                </span>
                <h3>{item.display_name}</h3>
                <p>
                  {item.environment_key === "testes"
                    ? "Ambiente isolado para versões candidatas e dados fictícios."
                    : "Recebe somente o artefacto integralmente aprovado em Testes."}
                </p>
                <strong>
                  Versão atual: {item.current_version ?? "ainda não definida"}
                </strong>
                {item.public_url && <small>{item.public_url}</small>}
              </article>
            ))}
          </div>
          <section className="version-audit">
            <div>
              <h3>Histórico auditável</h3>
              <p>
                Cada alteração conserva utilizador, data, ação e detalhe
                técnico. Este histórico não pode ser editado pela interface.
              </p>
            </div>
            {panel.events.length ? (
              <div className="version-audit-list">
                {panel.events.map((event) => (
                  <article key={event.id}>
                    <div>
                      <strong>{event.actor_name}</strong>
                      <span>
                        {new Date(event.created_at).toLocaleString("pt-PT")}
                      </span>
                    </div>
                    <b>{event.event_type.replaceAll("_", " ")}</b>
                    <code>{JSON.stringify(event.detail)}</code>
                  </article>
                ))}
              </div>
            ) : (
              <p className="version-empty">
                Ainda não existem eventos de versões.
              </p>
            )}
          </section>
        </>
      )}
      {section === "versions" && (
        <div className="version-workspace">
          <form onSubmit={create}>
            <h3>Carregar nova versão candidata</h3>
            <p>
              O artefacto fica privado e recebe uma impressão SHA-256. O número
              não pode ser repetido.
            </p>
            <label>
              <span>Versão *</span>
              <input
                value={form.version}
                onChange={(e) =>
                  setForm((v) => ({ ...v, version: e.target.value }))
                }
                pattern="[0-9]+\.[0-9]+\.[0-9]+([+-][a-zA-Z0-9.-]+)?"
                placeholder="Ex.: 0.34.0"
                required
              />
            </label>
            <label>
              <span>Nome da atualização *</span>
              <input
                value={form.title}
                onChange={(e) =>
                  setForm((v) => ({ ...v, title: e.target.value }))
                }
                required
              />
            </label>
            <label>
              <span>O que muda *</span>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm((v) => ({ ...v, description: e.target.value }))
                }
                required
              />
            </label>
            <label>
              <span>Migrações SQL</span>
              <textarea
                value={form.migrations}
                onChange={(e) =>
                  setForm((v) => ({ ...v, migrations: e.target.value }))
                }
                placeholder="Uma por linha ou separadas por vírgula"
              />
            </label>
            <label>
              <span>Edge Functions</span>
              <textarea
                value={form.functions}
                onChange={(e) =>
                  setForm((v) => ({ ...v, functions: e.target.value }))
                }
              />
            </label>
            <label>
              <span>Plano de retorno *</span>
              <textarea
                value={form.rollback}
                onChange={(e) =>
                  setForm((v) => ({ ...v, rollback: e.target.value }))
                }
                required
              />
            </label>
            <label className="version-file">
              <span>Artefacto *</span>
              <input
                type="file"
                accept=".html,.zip,.gz,.tar.gz"
                onChange={pick}
                required
              />
              <small>
                Máximo 50 MB · {file?.name ?? "nenhum ficheiro escolhido"}
              </small>
            </label>
            <button className="primary-button compact" disabled={loading}>
              Registar versão e calcular assinatura
            </button>
          </form>
          <div className="version-list">
            <h3>Ciclo de validação</h3>
            {panel.versions.length ? (
              panel.versions.map((version) => (
                <article key={version.id}>
                  <div className="version-heading">
                    <div>
                      <strong>
                        {version.version_number} · {version.title}
                      </strong>
                      <p>{version.description}</p>
                    </div>
                    <em>{labels[version.status] ?? version.status}</em>
                  </div>
                  <code>SHA-256 {version.artifact_sha256}</code>
                  <div className="version-checks">
                    {version.checks.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => void check(item)}
                        className={item.status}
                      >
                        {item.status === "approved" ? "✓" : "○"} {item.name}
                      </button>
                    ))}
                  </div>
                  <div className="version-actions">
                    <button onClick={() => void download(version)}>
                      Descarregar artefacto
                    </button>
                    {["draft", "corrections"].includes(version.status) && (
                      <button onClick={() => void install(version, "testes")}>
                        Confirmar instalação em Testes
                      </button>
                    )}
                    {version.status === "in_tests" &&
                      panel.permissions.publish_production &&
                      (version.checks.every(
                        (item) => item.status === "approved",
                      ) ? (
                        <button
                          onClick={() => void state(version.id, "approved")}
                        >
                          Aprovar para Produção
                        </button>
                      ) : (
                        <span className="version-blocked">
                          Conclua todas as validações para libertar Produção.
                        </span>
                      ))}
                    {version.status === "approved" &&
                      panel.permissions.publish_production && (
                        <button
                          onClick={() => {
                            setSchedule((value) => ({
                              ...value,
                              id: version.id,
                            }));
                            setSection("publication");
                          }}
                        >
                          Programar publicação
                        </button>
                      )}
                  </div>
                </article>
              ))
            ) : (
              <p className="version-empty">
                Ainda não existem versões registadas no Centro de Versões.
              </p>
            )}
          </div>
        </div>
      )}
      {section === "publication" && (
        <div className="publication-workspace">
          <section>
            <h3>Publicação controlada</h3>
            <p>
              O sistema nunca instala sozinho. Primeiro o agente técnico instala
              exatamente o artefacto aprovado; depois o utilizador autorizado
              confirma aqui a instalação real.
            </p>
          </section>
          {schedule.id && (
            <section className="schedule-form">
              <h3>Programar Produção</h3>
              <label>
                <span>Data e hora</span>
                <input
                  type="datetime-local"
                  value={schedule.date}
                  onChange={(e) =>
                    setSchedule((v) => ({ ...v, date: e.target.value }))
                  }
                />
              </label>
              <label>
                <span>Manutenção (minutos)</span>
                <input
                  type="number"
                  min="0"
                  value={schedule.minutes}
                  onChange={(e) =>
                    setSchedule((v) => ({ ...v, minutes: e.target.value }))
                  }
                />
              </label>
              <label>
                <span>Aviso</span>
                <input
                  value={schedule.message}
                  onChange={(e) =>
                    setSchedule((v) => ({ ...v, message: e.target.value }))
                  }
                />
              </label>
              <button
                className="primary-button compact"
                onClick={() => void plan()}
              >
                Guardar
              </button>
            </section>
          )}
          <div className="production-list">
            {panel.versions
              .filter((version) =>
                ["approved", "scheduled", "publishing"].includes(
                  version.status,
                ),
              )
              .map((version) => (
                <article key={version.id}>
                  <div>
                    <strong>
                      {version.version_number} · {version.title}
                    </strong>
                    <small>{labels[version.status]}</small>
                  </div>
                  <button
                    className="primary-button compact"
                    onClick={() => void install(version, "producao")}
                  >
                    Confirmar instalação real em Produção
                  </button>
                </article>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}
